import { useEffect } from "react";
import { type AxiosResponse, type AxiosError } from "axios";
import { SWRConfig } from "swr";
import { useCookies } from "react-cookie";
import {
  useNavigate,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "@/types/theme";
import { useScrollToTopOnNavigate } from "@/hooks/useScrollToTopOnNavigate";
import { Header, HEADER_HEIGHT } from "./features/header";
import { RealEstateReceptionBookFeed } from "./features/realEstateReceptionBookFeed";
import { Login } from "./features/login";
import { JwtCallback } from "./features/login/routes";
import {
  SalesforceOauthCallback,
  SalesforceOauthSuccess,
} from "./features/salesforceIntegration/routes";
import { type IJwtResponse } from "./types/jwt";
import { type ICookiesType } from "./types/cookies";
import { useApiClient } from "./hooks/useApiClient";
import { useLogin } from "./hooks/useLogin";
import { AccountSettings } from "./features/accountSettings";
import { AcquireBook } from "./features/acquireBook";
import { AcquireMultipleBooksRoutes } from "./features/acquireMultipleBooks";
import { AcquireBookStatus } from "./features/acquireBookStatus";
import { AcquireBookList } from "./features/acquireBookList";
import { OwnerInfoRoutes } from "./features/ownerInfo";
import { CommercialBookRoutes } from "./features/commercialBook";
import { CorporateMasterRoutes } from "./features/corporateMaster";
import { PropertyCodeAcquisitionRoutes } from "./features/propertyCodeAcquisition";
import { PropertyCodeBulkAcquisitionRoutes } from "./features/propertyCodeBulkAcquisition";
import { CommercialBookStatus } from "./features/commercialBookStatus";
import { ChibanViewerAccessControl } from "./features/map";
import { MapPlotRequestDetail } from "./features/mapPlotRequest/routes/MapPlotRequestDetail";
import { MapPlotRequestDetailFilesPage } from "./features/mapPlotRequest/routes/MapPlotRequestDetailFilesPage";
import { CustomerManagementRoutes } from "./features/customerManagement";
import { EmailManagementRoutes } from "./features/emailManagement/routes";
import { DmManagementRoutes } from "./features/dmManagement/routes";
import {
  useFeatureFlags,
  useIdentifyLaunchDarklyUser,
} from "./configs/featureFlag";
import { useCorporateMasterEnabled } from "./hooks/useCorporateMasterEnabled";
import { MypageRoutes } from "./features/mypage/routes";
import { UpdateChecker } from "./components/UpdateChecker";
import { StoredPictures } from "./features/storedpictures";
import { Maintenance } from "./features/maintenance/routes";
import { IpNotAllowed, IpRestrictionGuard } from "./features/ipNotAllowed";
import { MonitoringRoutes } from "@/features/monitoring";
import { CommercialMonitoringRoutes } from "@/features/commercialRegistrationMonitoring";
import { InheritanceReuseRoutes } from "@/features/inheritanceReuse";
import { INHERITANCE_REUSE_PATH } from "@/features/inheritanceReuse/utils/constants";
import { RegistryChangeAlertRoutes } from "@/features/registryChangeAlert";
import { REGISTRY_CHANGE_ALERT_PATH } from "@/features/registryChangeAlert/utils/constants";
import { useUserData, userDataContext } from "@/hooks/useUserData";
import { saveLoginRedirect } from "@/utils/auth";
import {
  IP_NOT_ALLOWED_PATH,
  isIpNotAllowedError,
  shouldHideAppNavigation,
} from "@/utils/ipRestriction";
import { TitleSearchRoutes } from "@/features/titleSearch";
import { RightsSectionOtsukuRoutes } from "@/features/rightsSectionOtsuku";
import { AllMattersRoutes } from "@/features/allMatters";
import { CommercialPictureAnalysisRoutes } from "@/features/commercialPictureAnalysis";
import { PublicLandPriceRoutes } from "@/features/publicLandPrice";
import { PropertySummaryRoutes } from "@/features/propertySummary";
import { CommercialRegistrationReceptionBookFeed } from "@/features/commercialRegistrationReceptionBookFeed";
import { ReassistPage } from "@/features/reassistRequests";
import { ReassistSalePage } from "@/features/reassistSale";
import { RealEstateDocumentFetchPage } from "@/features/realEstateDocumentFetch/RealEstateDocumentFetchPage";
import { RealEstateResearchRequestPage } from "@/features/realEstateResearchRequest/RealEstateResearchRequestPage";
import { SideMenu } from "@/components/SideMenu";
import { LoginAnnouncementModal } from "@/features/banner/components/LoginAnnouncementModal";
import { Box } from "@mui/material";
import { SideMenuStateProvider } from "@/hooks/useSideMenuState";
import { ContactFormDialogProvider } from "@/hooks/useContactFormDialog";

const App: React.FC = () => {
  const {
    commercialBookList,
    datalensBulkPictureRequest,
    datalensPictureRequest,
    commercialRegistrationReceptionBook,
    commercialPictureAnalysis,
    commercialRegistrationMonitoring,
    customerManagement,
    dmManagement,
    emailManagement,
    mypageTop,
    realEstateRegistrationAggregationOfNames,
    allMattersAnalysis,
    maintenanceMode,
    realEstateRegistrationMonitoring,
    realEstateTitle,
    rightsSectionOtsukuSearch,
    inheritanceReuseCandidates,
    registryChangeAlert,
    reassistArchive,
    realEstateResearchRequest,
  } = useFeatureFlags();
  const showCorporateMaster = useCorporateMasterEnabled();

  // ---
  // hooks群
  // ---
  const [cookies, setCookie] = useCookies(); // eslint-disable-line
  const typedCookies = cookies as ICookiesType;
  const location = useLocation();
  const navigate = useNavigate();
  useScrollToTopOnNavigate();
  const { apiClient } = useApiClient();
  const { isLoggedIn } = useLogin();
  // 許可IP外の案内ではログイン状態が残るため、明示的にアプリのナビゲーションを隠す
  const hideAppNavigation = shouldHideAppNavigation(location.pathname);
  const userData = useUserData();
  useIdentifyLaunchDarklyUser(isLoggedIn, userData.data);

  // ---
  // useEffect群
  // ---
  useEffect(() => {
    (async () => {
      // メンテナンスモードの場合は通知を出さないようにバイパスする
      if (maintenanceMode) {
        return;
      }

      if (isLoggedIn) {
        if (cookies.accesstoken === undefined) {
          toast.warning("ログインしてください");
          setCookie("isLogin", "false", { path: "/" });
          navigate("/login");
        } else {
          await verifyAccessToken();
        }
      } else if (
        location.pathname !== "/login" &&
        location.pathname !== "/auth/jwt-callback" &&
        // 許可IP外で追い出された利用者をログイン画面へ引き戻さない
        location.pathname !== IP_NOT_ALLOWED_PATH
      ) {
        // 未ログインでURL直打ちした場合ログイン画面に遷移する
        redirectToLogin();
      }
    })();
  }, []);

  // ---
  // function群
  // ---
  const redirectToLogin = (): void => {
    toast.warning("ログインしてください");
    setCookie("isLogin", "false", { path: "/" });
    saveLoginRedirect(location.pathname + location.search);
    navigate("/login");
  };

  const refreshToken = async (): Promise<void> => {
    await apiClient
      .post("auth/jwt/refresh", {
        refresh: typedCookies.refreshtoken,
      })
      .then((res: AxiosResponse<IJwtResponse>) => {
        setCookie("accesstoken", res.data.access, { path: "/" });
        setCookie("refreshtoken", res.data.refresh, { path: "/" });
      })
      .catch((error) => {
        // 許可IP外はインターセプタで専用画面へ遷移済みのため、ログイン画面へ引き戻さない
        if (isIpNotAllowedError(error)) {
          return;
        }

        // リレッシュトークンが期限切れの場合はログイン画面に遷移
        redirectToLogin();
      });
  };

  const verifyAccessToken = async (): Promise<void> => {
    await apiClient
      .post("auth/jwt/verify", {
        token: typedCookies.accesstoken,
      })
      .catch(async (err: AxiosError) => {
        if (err.response != null && err.response.status === 401) {
          // アクセストークンが期限切れの場合は
          // リフレッシュトークンを用いて新しいアクセストークンとリフレッシュトークンを取得する
          await refreshToken();
        }
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <userDataContext.Provider value={userData}>
        <SideMenuStateProvider>
          <ContactFormDialogProvider>
            <UpdateChecker>
              <SWRConfig
                value={{
                  errorRetryCount: 1,
                  onError: (error: AxiosError) => {
                    // 許可IP外は専用画面へ遷移済みのため、汎用エラーを重ねて出さない
                    if (
                      error.response?.status !== 401 &&
                      !isIpNotAllowedError(error)
                    )
                      toast.error("予期しないエラーが発生しました");
                  },
                }}
              >
                {isLoggedIn && !hideAppNavigation ? <Header /> : null}
                <Box
                  sx={{
                    display: "flex",
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                  }}
                >
                  {isLoggedIn && !hideAppNavigation ? <SideMenu /> : null}
                  <Box
                    component="main"
                    sx={{ flexGrow: 1, overflow: "visible", minWidth: 0 }}
                  >
                    <IpRestrictionGuard>
                      <Routes>
                        {/* メンテモードの場合は全てのルートをメンテナンス画面に遷移させる */}
                        {maintenanceMode ? (
                          <>
                            <Route
                              path="*"
                              element={<Navigate replace to="/maintenance" />}
                            />
                            <Route
                              path="/maintenance"
                              element={<Maintenance />}
                            />
                          </>
                        ) : (
                          <>
                            <Route
                              path="/"
                              element={<Navigate replace to="/login" />}
                            />
                            <Route path={`/login`} element={<Login />} />
                            {/* 許可IP外の案内。未ログインでも表示するためログイン画面と同列に置く */}
                            <Route
                              path={IP_NOT_ALLOWED_PATH}
                              element={<IpNotAllowed />}
                            />
                            <Route
                              path={`/auth/jwt-callback`}
                              element={<JwtCallback />}
                            />
                            {/* Salesforce OAuthコールバック。認証途中でフラグが切り替わると
                              処理が中断されるため、機能フラグでは隠さない */}
                            <Route
                              path="/auth/callback/on-salesforce"
                              element={<SalesforceOauthCallback />}
                            />
                            <Route
                              path="/auth/success/on-salesforce"
                              element={<SalesforceOauthSuccess />}
                            />
                            {mypageTop && (
                              <Route
                                path={`/mypage/*`}
                                element={<MypageRoutes />}
                              />
                            )}
                            <Route
                              path={`/feed`}
                              element={<RealEstateReceptionBookFeed />}
                            />
                            <Route
                              path={`/account`}
                              element={<AccountSettings />}
                            />
                            <Route
                              path={`/acquirebook`}
                              element={<AcquireBook />}
                            />
                            <Route
                              path={`/acquiremultiplebooks/*`}
                              element={<AcquireMultipleBooksRoutes />}
                            />
                            {/* 静的セグメントはワイルドカードより優先されるため、
                              上の /datalens/picturerequest/* と共存できる */}
                            {datalensBulkPictureRequest ? (
                              <Route
                                path="/datalens/picturerequest/multiple"
                                element={<PropertyCodeBulkAcquisitionRoutes />}
                              />
                            ) : null}
                            <Route
                              path={`/acquirebookstatus/:id`}
                              element={<AcquireBookStatus />}
                            />
                            <Route
                              path={`/acquirebooklist`}
                              element={<AcquireBookList />}
                            />
                            <Route
                              path={`/ownerinfo/*`}
                              element={<OwnerInfoRoutes />}
                            />
                            {allMattersAnalysis ? (
                              <Route
                                path={`/allmatters/*`}
                                element={<AllMattersRoutes />}
                              />
                            ) : null}
                            {commercialPictureAnalysis ? (
                              <Route
                                path={`/commercialpictureanalysis/*`}
                                element={<CommercialPictureAnalysisRoutes />}
                              />
                            ) : null}
                            {commercialBookList ? (
                              <Route
                                path={`/commercialbook/*`}
                                element={<CommercialBookRoutes />}
                              />
                            ) : null}
                            {showCorporateMaster ? (
                              <Route
                                path={`/corporate-master/*`}
                                element={<CorporateMasterRoutes />}
                              />
                            ) : null}
                            {commercialBookList ? (
                              <Route
                                path={`/commercialbookstatus/:id`}
                                element={<CommercialBookStatus />}
                              />
                            ) : null}
                            {datalensPictureRequest ? (
                              <Route
                                path="/datalens/picturerequest/*"
                                element={<PropertyCodeAcquisitionRoutes />}
                              />
                            ) : null}
                            <Route
                              path="/map"
                              element={<ChibanViewerAccessControl />}
                            />
                            <Route
                              path="/reassist"
                              element={<ReassistPage />}
                            />
                            {reassistArchive === true ? (
                              <Route
                                path="/reassist/archive"
                                element={<ReassistSalePage />}
                              />
                            ) : null}
                            <Route
                              path="/realestatedocumentfetch"
                              element={<RealEstateDocumentFetchPage />}
                            />
                            {realEstateResearchRequest === true ? (
                              <Route
                                path="/real-estate-research-request"
                                element={<RealEstateResearchRequestPage />}
                              />
                            ) : null}
                            <Route
                              path="/mapplotrequest/:id"
                              element={<MapPlotRequestDetail />}
                            />
                            <Route
                              path="/mapplotrequest/:id/details/:detailId/files"
                              element={<MapPlotRequestDetailFilesPage />}
                            />
                            {/* 顧客管理 */}
                            {customerManagement ? (
                              <Route
                                path={`/customermanagement/*`}
                                element={<CustomerManagementRoutes />}
                              />
                            ) : null}
                            {/* メール管理 */}
                            {emailManagement ? (
                              <Route
                                path={`/emailManagement/*`}
                                element={<EmailManagementRoutes />}
                              />
                            ) : null}
                            {/* DM管理 */}
                            {dmManagement ? (
                              <Route
                                path={`/dmManagement/*`}
                                element={<DmManagementRoutes />}
                              />
                            ) : null}
                            {/* 名寄せ */}
                            {realEstateRegistrationAggregationOfNames ? (
                              <Route
                                path={`/storedpictures/*`}
                                element={<StoredPictures />}
                              />
                            ) : null}
                            {/* モニタリング */}
                            {realEstateRegistrationMonitoring ? (
                              <Route
                                path={`/monitoring/*`}
                                element={<MonitoringRoutes />}
                              />
                            ) : null}
                            {/* 商業登記モニタリング */}
                            {commercialRegistrationMonitoring ? (
                              <Route
                                path={`/commercial-monitoring/*`}
                                element={<CommercialMonitoringRoutes />}
                              />
                            ) : null}
                            {/* 相続後リユース候補（フラグ付与時にマウント。月額会員判定は
                                InheritanceReuseRoutes 内のガードで実施し、非会員は /mypage へ退避。
                                会員情報ロード中にルートを外すとワイルドカードが /login へ転送するため、
                                ここでは会員判定でルートを出し入れしない） */}
                            {inheritanceReuseCandidates ? (
                              <Route
                                path={`${INHERITANCE_REUSE_PATH}/*`}
                                element={<InheritanceReuseRoutes />}
                              />
                            ) : null}
                            {/* 取得済み登記の変動アラート */}
                            {registryChangeAlert ? (
                              <Route
                                path={`${REGISTRY_CHANGE_ALERT_PATH}/*`}
                                element={<RegistryChangeAlertRoutes />}
                              />
                            ) : null}
                            {/* 表題部検索 */}
                            {realEstateTitle ? (
                              <Route
                                path={`/titlesearch/*`}
                                element={<TitleSearchRoutes />}
                              />
                            ) : null}
                            {/* 権利部（乙区）情報検索 */}
                            {rightsSectionOtsukuSearch ? (
                              <Route
                                path={`/rightssectionotsuku/*`}
                                element={<RightsSectionOtsukuRoutes />}
                              />
                            ) : null}
                            {/* 公示価格詳細表示 */}
                            <Route
                              path={`/publiclandprice/*`}
                              element={<PublicLandPriceRoutes />}
                            />
                            {/* 物件概要書 */}
                            <Route
                              path={`/propertysummary/*`}
                              element={<PropertySummaryRoutes />}
                            />
                            {/* 商業登記受付帳 */}
                            {commercialRegistrationReceptionBook ? (
                              <Route
                                path={`/commercialregistrationreceptionbook/*`}
                                element={
                                  <CommercialRegistrationReceptionBookFeed />
                                }
                              />
                            ) : null}
                            {/* その他 */}
                            <Route
                              path="*"
                              element={<Navigate replace to="/login" />}
                            />
                          </>
                        )}
                      </Routes>
                    </IpRestrictionGuard>
                  </Box>
                </Box>
                {isLoggedIn && !hideAppNavigation && !maintenanceMode ? (
                  <LoginAnnouncementModal />
                ) : null}
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  newestOnTop
                  closeOnClick
                  pauseOnFocusLoss
                  draggable={false}
                  pauseOnHover
                  theme="light"
                />
              </SWRConfig>
            </UpdateChecker>
          </ContactFormDialogProvider>
        </SideMenuStateProvider>
      </userDataContext.Provider>
    </ThemeProvider>
  );
};

export { App };
